import { useMemo, useState } from 'react'
import { REGIONS, RESORTS } from '../data/resorts'
import { IllustrationSlot } from '../components/IllustrationSlot'
import { MapDots } from '../components/MapDots'
import { PassChip } from '../components/PassChip'
import { useStore } from '../lib/state'
import type { PassId } from '../data/passes'
import type { Stamp, StampStatus } from '../lib/types'

export function Mountains() {
  const { state, setStamp } = useStore()
  const [q, setQ] = useState('')
  const [region, setRegion] = useState('all')
  const [passFilter, setPassFilter] = useState<'all' | PassId>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | StampStatus | 'unset'>('all')
  const [dateDraft, setDateDraft] = useState<Record<string, string>>({})
  const [view, setView] = useState<'map' | 'list'>('map')

  const stampMap = useMemo(() => {
    const m = new Map<string, Stamp>()
    for (const s of state.stamps) m.set(s.resortId, s)
    return m
  }, [state.stamps])

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase()
    return RESORTS.filter((r) => {
      if (region !== 'all' && r.region !== region) return false
      if (passFilter !== 'all' && r.pass !== passFilter) return false
      const st = stampMap.get(r.id)
      if (statusFilter === 'visited' && st?.status !== 'visited') return false
      if (statusFilter === 'want' && st?.status !== 'want') return false
      if (statusFilter === 'unset' && st) return false
      if (!qq) return true
      return (
        r.name.toLowerCase().includes(qq) ||
        r.state.toLowerCase().includes(qq) ||
        r.region.toLowerCase().includes(qq)
      )
    })
  }, [q, region, passFilter, statusFilter, stampMap])

  return (
    <div className="stack">
      <div className="card stack">
        <div className="row between" style={{ alignItems: 'flex-start' }}>
          <div className="page-head">
            <h2>Stamp mountains</h2>
            <p className="muted">
              {RESORTS.length} resorts · tap the map or stamp from the list
            </p>
          </div>
          <IllustrationSlot kind="passport" caption="art later" />
        </div>
        <div className="row">
          <button
            type="button"
            className={`btn sm${view === 'map' ? ' primary' : ''}`}
            onClick={() => setView('map')}
          >
            Map
          </button>
          <button
            type="button"
            className={`btn sm${view === 'list' ? ' primary' : ''}`}
            onClick={() => setView('list')}
          >
            List
          </button>
        </div>
        <div className="filters">
          <input
            className="search"
            placeholder="Search resorts…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="all">All regions</option>
            {REGIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select
            value={passFilter}
            onChange={(e) => setPassFilter(e.target.value as typeof passFilter)}
          >
            <option value="all">Any pass</option>
            <option value="epic">Epic</option>
            <option value="ikon">Ikon</option>
            <option value="indy">Indy</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          >
            <option value="all">All stamps</option>
            <option value="visited">Visited</option>
            <option value="want">Wishlist</option>
            <option value="unset">Unstamped</option>
          </select>
        </div>

        {view === 'map' && (
          <MapDots stamps={state.stamps} onStamp={(id, status) => setStamp(id, status)} />
        )}
      </div>

      {view === 'list' && (
        <>
          <div className="resort-grid">
            {filtered.map((r) => {
              const st = stampMap.get(r.id)
              const cls = st?.status === 'visited' ? 'visited' : st?.status === 'want' ? 'want' : ''
              return (
                <article key={r.id} className={`resort-card ${cls}`}>
                  {st?.status === 'visited' && <span className="stamp-seal">Skied</span>}
                  {st?.status === 'want' && <span className="stamp-seal">Want</span>}
                  <div className="row between">
                    <div style={{ paddingRight: 16 }}>
                      <div className="resort-name">{r.name}</div>
                      <div className="resort-meta">
                        {r.state}, {r.country} · {r.region}
                      </div>
                    </div>
                    <PassChip id={r.pass === 'mixed' ? 'none' : r.pass} />
                  </div>
                  <div className="field">
                    <label>Date (optional)</label>
                    <input
                      type="date"
                      value={dateDraft[r.id] ?? st?.date ?? ''}
                      onChange={(e) => setDateDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                    />
                  </div>
                  <div className="row">
                    <button
                      className={`btn cta-stamp ${st?.status === 'visited' ? 'active-good' : 'primary'}`}
                      onClick={() =>
                        setStamp(
                          r.id,
                          st?.status === 'visited' ? null : 'visited',
                          dateDraft[r.id] || st?.date,
                        )
                      }
                    >
                      {st?.status === 'visited' ? '✓ Visited' : 'Stamp visited'}
                    </button>
                    <button
                      className={`btn cta-stamp ${st?.status === 'want' ? 'active-want' : ''}`}
                      onClick={() =>
                        setStamp(
                          r.id,
                          st?.status === 'want' ? null : 'want',
                          dateDraft[r.id] || st?.date,
                        )
                      }
                    >
                      {st?.status === 'want' ? '★ Wishlist' : 'Want to go'}
                    </button>
                  </div>
                </article>
              )
            })}
          </div>
          {filtered.length === 0 && <div className="empty">No resorts match those filters.</div>}
        </>
      )}
    </div>
  )
}
